<template>
  <div class="my-openings container py-5">
    <div v-if="isAuthorized">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <div>
          <h2 class="mb-1">Les permanences</h2>
          <p class="text-muted mb-0">
            Retrouvez les permanences à venir et celles passées. 
          </p>
        </div>
        <div class="d-flex flex-column flex-md-row mt-3 mt-md-0">
          <b-button
            variant="outline-primary"
            class="mr-md-3 mb-2 mb-md-0"
            @click="openLocalCodeModal"
          >
            <b-icon icon="key-fill" class="mr-1"></b-icon>
            Code du local
          </b-button>
          <!-- <b-button
            variant="outline-secondary"
            class="mr-md-3 mb-2 mb-md-0"
            @click="fetchOpenings"
            :disabled="loading"
          >
            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
            Actualiser
          </b-button> -->
          <b-button variant="primary" @click="addOpeningVisible = true">
            <b-icon icon="plus"></b-icon>
            Proposer une permanence
          </b-button>
        </div>
      </div>

      <b-alert v-if="error" variant="danger" show>
        {{ error }}
      </b-alert>

      <div v-if="loading" class="text-center py-5">
        <b-spinner label="Chargement..."></b-spinner>
      </div>

      <div v-else>
        <b-alert v-if="!MyOpenings.length && !OtherOpenings.length && !PastOpenings.length" variant="info" show>
          Il n'y a pas encore de permanence planifiée. Utilisez le bouton <strong>Proposer une permanence</strong>
          pour en créer une.
        </b-alert>

        <div v-else>
          <section class="openings-section mb-5">
            <h4 class="section-title mb-3">Mes permanences à venir</h4>
            <p v-if="!MyOpenings.length" class="text-muted">
              Aucune permanence à venir que vous avez créée.
            </p>
            <div v-else class="table-responsive">
              <table class="table table-hover align-middle d-none d-md-table">
                <thead>
                  <tr>
                    <th>{{ isAdmin ? 'Type' : 'Statut' }}</th>
                    <th>Date</th>
                    <th>Titre</th>
                    <th>Événements</th>
                    <th>Participants</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="opening in MyOpenings" :key="`my-upcoming-${opening.id}`">
                    <td v-if="isAdmin">
                      <b-badge :variant="getOpeningTypeVariant(opening.openingType)">
                        {{ getOpeningTypeLabel(opening.openingType) }}
                      </b-badge>
                    </td>
                    <td v-else>
                      <b-badge :variant="isOpeningPast(opening) ? 'secondary' : 'success'">
                        {{ isOpeningPast(opening) ? 'Terminée' : 'À venir' }}
                      </b-badge>
                    </td>
                    <td>{{ formatDate(opening.date) }}</td>
                    <td>{{ opening.title || "-" }}</td>
                    <td>
                      {{ opening.openingEvents ? opening.openingEvents.length : 0 }}
                    </td>
                    <td>
                      {{ getParticipantsCount(opening) }} {{ getParticipantsCount(opening) > 1 ? 'participants' : 'participant' }}
                    </td>
                    <td class="text-right">
                      <b-dropdown
                        class="action-menu"
                        right
                        size="sm"
                        variant="link"
                        boundary="window"
                        append-to-body
                        no-caret
                        toggle-class="px-2 action-menu-toggle"
                      >
                        <template #button-content>
                          <b-icon icon="three-dots-vertical"></b-icon>
                        </template>
                        <b-dropdown-item @click="openDetails(opening)">
                          <b-icon icon="info-circle" class="mr-2"></b-icon>
                          Détails
                        </b-dropdown-item>
                        <b-dropdown-item @click="goToOpening(opening)">
                          <b-icon icon="gear-fill" class="mr-2"></b-icon>
                          Gérer la permanence
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="canDeleteOpening(opening)"
                          variant="danger"
                          @click="openDeleteConfirmation(opening)"
                        >
                          <b-icon icon="trash" class="mr-2"></b-icon>
                          Supprimer
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="d-md-none">
                <b-card
                  v-for="opening in MyOpenings"
                  :key="`my-upcoming-card-${opening.id}`"
                  class="opening-mobile-card mb-3"
                >
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div class="opening-mobile-card__title">{{ opening.title || "-" }}</div>
                      <div class="text-muted small">{{ formatDate(opening.date) }}</div>
                    </div>
                    <div class="opening-mobile-card__actions">
                      <b-button
                        size="sm"
                        variant="link"
                        class="p-0 text-secondary opening-mobile-action-btn"
                        @click="openDetails(opening)"
                        aria-label="Détails"
                      >
                        <b-icon icon="info-circle"></b-icon>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="link"
                        class="p-0 text-primary ml-2 opening-mobile-action-btn"
                        @click="goToOpening(opening)"
                        aria-label="Gérer la permanence"
                      >
                        <b-icon icon="gear-fill"></b-icon>
                      </b-button>
                      <b-button
                        v-if="canDeleteOpening(opening)"
                        size="sm"
                        variant="link"
                        class="p-0 text-danger ml-2 opening-mobile-action-btn"
                        @click="openDeleteConfirmation(opening)"
                        aria-label="Supprimer"
                      >
                        <b-icon icon="trash"></b-icon>
                      </b-button>
                    </div>
                  </div>
                  <div class="mb-2">
                    <b-badge v-if="isAdmin" :variant="getOpeningTypeVariant(opening.openingType)">
                      {{ getOpeningTypeLabel(opening.openingType) }}
                    </b-badge>
                    <b-badge v-else :variant="isOpeningPast(opening) ? 'secondary' : 'success'">
                      {{ isOpeningPast(opening) ? 'Terminée' : 'À venir' }}
                    </b-badge>
                  </div>
                  <div class="opening-mobile-card__meta">
                    <div class="opening-mobile-card__row">
                      <span class="opening-mobile-card__label">Événements</span>
                      <span class="opening-mobile-card__value">{{ opening.openingEvents ? opening.openingEvents.length : 0 }}</span>
                    </div>
                    <div class="opening-mobile-card__row">
                      <span class="opening-mobile-card__label">Participants</span>
                      <span class="opening-mobile-card__value">
                        {{ getParticipantsCount(opening) }} {{ getParticipantsCount(opening) > 1 ? 'participants' : 'participant' }}
                      </span>
                    </div>
                  </div>
                </b-card>
              </div>
            </div>
          </section>

          <section class="openings-section mb-5">
            <h4 class="section-title mb-3">Les autres permanences à venir</h4>
            <p v-if="!OtherOpenings.length" class="text-muted">
              Aucune autre permanence à venir.
            </p>
            <div v-else class="table-responsive">
              <table class="table table-hover align-middle d-none d-md-table">
                <thead>
                  <tr>
                    <th>{{ isAdmin ? 'Type' : 'Statut' }}</th>
                    <th>Date</th>
                    <th>Titre</th>
                    <th>Événements</th>
                    <th>Participants</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="opening in OtherOpenings" :key="`other-upcoming-${opening.id}`">
                    <td v-if="isAdmin">
                      <b-badge :variant="getOpeningTypeVariant(opening.openingType)">
                        {{ getOpeningTypeLabel(opening.openingType) }}
                      </b-badge>
                    </td>
                    <td v-else>
                      <b-badge :variant="isOpeningPast(opening) ? 'secondary' : 'success'">
                        {{ isOpeningPast(opening) ? 'Terminée' : 'À venir' }}
                      </b-badge>
                    </td>
                    <td>{{ formatDate(opening.date) }}</td>
                    <td>{{ opening.title || "-" }}</td>
                    <td>
                      {{ opening.openingEvents ? opening.openingEvents.length : 0 }}
                    </td>
                    <td>
                      {{ getParticipantsCount(opening) }} {{ getParticipantsCount(opening) > 1 ? 'participants' : 'participant' }}
                    </td>
                    <td class="text-right">
                      <b-dropdown
                        class="action-menu"
                        right
                        size="sm"
                        variant="link"
                        boundary="window"
                        append-to-body
                        no-caret
                        toggle-class="px-2 action-menu-toggle"
                      >
                        <template #button-content>
                          <b-icon icon="three-dots-vertical"></b-icon>
                        </template>
                        <b-dropdown-item @click="openDetails(opening)">
                          <b-icon icon="info-circle" class="mr-2"></b-icon>
                          Détails
                        </b-dropdown-item>
                        <b-dropdown-item v-if="isAdmin" @click="goToOpening(opening)">
                          <b-icon icon="gear-fill" class="mr-2"></b-icon>
                          Gérer la permanence
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="canDeleteOpening(opening)"
                          variant="danger"
                          @click="openDeleteConfirmation(opening)"
                        >
                          <b-icon icon="trash" class="mr-2"></b-icon>
                          Supprimer
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="d-md-none">
                <b-card
                  v-for="opening in OtherOpenings"
                  :key="`other-upcoming-card-${opening.id}`"
                  class="opening-mobile-card mb-3"
                >
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div class="opening-mobile-card__title">{{ opening.title || "-" }}</div>
                      <div class="text-muted small">{{ formatDate(opening.date) }}</div>
                    </div>
                    <div class="opening-mobile-card__actions">
                      <b-button
                        size="sm"
                        variant="link"
                        class="p-0 text-secondary opening-mobile-action-btn"
                        @click="openDetails(opening)"
                        aria-label="Détails"
                      >
                        <b-icon icon="info-circle"></b-icon>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="link"
                        class="p-0 text-primary ml-2 opening-mobile-action-btn"
                        @click="goToOpening(opening)"
                        aria-label="Gérer la permanence"
                      >
                        <b-icon icon="gear-fill"></b-icon>
                      </b-button>
                      <b-button
                        v-if="canDeleteOpening(opening)"
                        size="sm"
                        variant="link"
                        class="p-0 text-danger ml-2 opening-mobile-action-btn"
                        @click="openDeleteConfirmation(opening)"
                        aria-label="Supprimer"
                      >
                        <b-icon icon="trash"></b-icon>
                      </b-button>
                    </div>
                  </div>
                  <div class="mb-2">
                    <b-badge v-if="isAdmin" :variant="getOpeningTypeVariant(opening.openingType)">
                      {{ getOpeningTypeLabel(opening.openingType) }}
                    </b-badge>
                    <b-badge v-else :variant="isOpeningPast(opening) ? 'secondary' : 'success'">
                      {{ isOpeningPast(opening) ? 'Terminée' : 'À venir' }}
                    </b-badge>
                  </div>
                  <div class="opening-mobile-card__meta">
                    <div class="opening-mobile-card__row">
                      <span class="opening-mobile-card__label">Événements</span>
                      <span class="opening-mobile-card__value">{{ opening.openingEvents ? opening.openingEvents.length : 0 }}</span>
                    </div>
                    <div class="opening-mobile-card__row">
                      <span class="opening-mobile-card__label">Participants</span>
                      <span class="opening-mobile-card__value">
                        {{ getParticipantsCount(opening) }} {{ getParticipantsCount(opening) > 1 ? 'participants' : 'participant' }}
                      </span>
                    </div>
                  </div>
                </b-card>
              </div>
            </div>
          </section>

          <section class="openings-section past-openings" v-if="isAdmin">
            <h4 class="section-title mb-3">Mes permanences passées</h4>
            <p v-if="!PastOpenings.length" class="text-muted">
              Aucune permanence passée que vous avez créée.
            </p>
            <div v-else class="table-responsive">
              <table class="table table-hover align-middle d-none d-md-table">
                <thead>
                  <tr>
                    <th>{{ isAdmin ? 'Type' : 'Statut' }}</th>
                    <th>Date</th>
                    <th>Titre</th>
                    <th>Événements</th>
                    <th>Participants</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="opening in PastOpenings" :key="`my-past-${opening.id}`">
                    <td v-if="isAdmin">
                      <b-badge :variant="getOpeningTypeVariant(opening.openingType)">
                        {{ getOpeningTypeLabel(opening.openingType) }}
                      </b-badge>
                    </td>
                    <td v-else>
                      <b-badge :variant="isOpeningPast(opening) ? 'secondary' : 'success'">
                        {{ isOpeningPast(opening) ? 'Terminée' : 'À venir' }}
                      </b-badge>
                    </td>
                    <td>{{ formatDate(opening.date) }}</td>
                    <td>{{ opening.title || "-" }}</td>
                    <td>
                      {{ opening.openingEvents ? opening.openingEvents.length : 0 }}
                    </td>
                    <td>
                      {{ getParticipantsCount(opening) }} participant(s)
                    </td>
                    <td class="text-right">
                      <b-dropdown
                        class="action-menu"
                        right
                        size="sm"
                        variant="link"
                        boundary="window"
                        append-to-body
                        no-caret
                        toggle-class="px-2 action-menu-toggle"
                      >
                        <template #button-content>
                          <b-icon icon="three-dots-vertical"></b-icon>
                        </template>
                        <b-dropdown-item @click="openDetails(opening)">
                          <b-icon icon="info-circle" class="mr-2"></b-icon>
                          Détails
                        </b-dropdown-item>
                        <b-dropdown-item @click="goToOpening(opening)">
                          <b-icon icon="gear-fill" class="mr-2"></b-icon>
                          Gérer la permanence
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="canDeleteOpening(opening)"
                          variant="danger"
                          @click="openDeleteConfirmation(opening)"
                        >
                          <b-icon icon="trash" class="mr-2"></b-icon>
                          Supprimer
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="d-md-none">
                <b-card
                  v-for="opening in PastOpenings"
                  :key="`my-past-card-${opening.id}`"
                  class="opening-mobile-card mb-3"
                >
                  <div class="mb-2">
                    <div>
                      <div class="opening-mobile-card__title">{{ opening.title || "-" }}</div>
                      <div class="text-muted small">{{ formatDate(opening.date) }}</div>
                    </div>
                  </div>
                  <div class="mb-2">
                    <b-badge v-if="isAdmin" :variant="getOpeningTypeVariant(opening.openingType)">
                      {{ getOpeningTypeLabel(opening.openingType) }}
                    </b-badge>
                    <b-badge v-else :variant="isOpeningPast(opening) ? 'secondary' : 'success'">
                      {{ isOpeningPast(opening) ? 'Terminée' : 'À venir' }}
                    </b-badge>
                  </div>
                  <div class="opening-mobile-card__meta">
                    <div class="opening-mobile-card__row">
                      <span class="opening-mobile-card__label">Événements</span>
                      <span class="opening-mobile-card__value">{{ opening.openingEvents ? opening.openingEvents.length : 0 }}</span>
                    </div>
                    <div class="opening-mobile-card__row">
                      <span class="opening-mobile-card__label">Participants</span>
                      <span class="opening-mobile-card__value">{{ getParticipantsCount(opening) }} participant(s)</span>
                    </div>
                  </div>
                </b-card>
              </div>
            </div>
          </section>
        </div>
      </div>
      
      <div>
        <b-modal id="confirm-delete-modal" @hide="confirmModalClosed()">
          <template #modal-title>Confirmer la suppression</template>
          
          <b-alert v-if="confirmedOpeningHasOtherEvents" show variant="warning">
            Un autre ouvreur a inscrit un créneau sur cette permanence. Vous ne pouvez pas la supprimer tant que des créneaux appartenant à d'autres ouvreurs existent. Organisez-vous avec eux pour répartir ou supprimer les créneaux concernés.
          </b-alert>

          <template v-else>
            <p>Êtes-vous sûr de vouloir supprimer cette permanence ? Cette action est irréversible.</p>
            <p>Veuillez taper <strong>"SUPPRIMER"</strong> pour confirmer :</p>
            <b-input v-model="confirmDeleteText" placeholder="SUPPRIMER"/>
          </template>

          <template #modal-footer>
            <b-button variant="secondary" @click="$bvModal.hide('confirm-delete-modal')">Annuler</b-button>

            <b-button v-if="!confirmedOpeningHasOtherEvents" variant="danger" @click="deleteOpening(confirmedOpening)"
              :disabled="confirmDeleteText !== 'SUPPRIMER'"
            >
              <b-icon icon="trash"></b-icon>
            </b-button>

          </template>
        </b-modal>
      </div>

      <b-modal
        v-model="detailsModalVisible"
        title="Détails de la permanence"
        hide-footer
        centered
        size="lg"
        scrollable
      >
        <div v-if="selectedOpening" class="details-modal">
          <div class="details-header d-flex justify-content-between align-items-start mb-3">
            <div>
              <h4 class="mb-1">{{ selectedOpening.title || "Permanence" }}</h4>
              <div class="text-muted">{{ formatDate(selectedOpening.date) }}</div>
            </div>
            <b-badge :variant="selectedOpening.isPast ? 'secondary' : 'success'">
              {{ selectedOpening.isPast ? 'Terminée' : 'À venir' }}
            </b-badge>
          </div>

          <div class="details-meta d-flex flex-wrap">
            <div class="meta-item">
              <span class="text-muted">Participants</span>
              <div class="meta-value">{{ getParticipantsCount(selectedOpening) }}</div>
            </div>
            <div class="meta-item">
              <span class="text-muted">Créneaux</span>
              <div class="meta-value">{{ detailsForm.events.length }}</div>
            </div>
          </div>

          <b-card class="details-card mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">Créneaux</h6>
              <b-button size="sm" variant="outline-primary" :disabled="pendingDetailsEvent !== null" @click="addDetailsEvent">
                + Ajouter un créneau
              </b-button>
            </div>

            <p v-if="!detailsForm.events.length" class="text-muted mb-3">
              Aucun événement défini.
            </p>

            <div
              v-for="(event, index) in detailsForm.events"
              :key="`detail-event-${index}`"
              class="details-event-row"
              :class="{ 'event-row--locked': pendingDetailsEvent !== null }"
            >
              <div class="details-event-main flex-grow-1">
                <div class="d-flex align-items-center">
                  <HourMinutePicker
                    v-model="event.time"
                    class="mr-2 details-time-input"
                    :disabled="!canCurrentUserDeleteEvent(event) || pendingDetailsEvent !== null"
                    @change="sortDetailsEvents"
                  />
                  <b-form-select
                    v-model="event.type"
                    :options="eventTypes"
                    class="mr-2 details-type-input"
                    :disabled="!canCurrentUserDeleteEvent(event) || pendingDetailsEvent !== null"
                  ></b-form-select>
                  <b-button
                    v-if="canCurrentUserDeleteEvent(event)"
                    variant="outline-danger"
                    @click="removeDetailsEvent(index)"
                    :disabled="detailsForm.events.length <= 2 || pendingDetailsEvent !== null"
                  >
                    <b-icon icon="trash"></b-icon>
                  </b-button>
                  <small v-else class="text-muted mr-2">
                    de {{ getEventCreatorLabel(event) }}
                  </small>
                </div>
              </div>
            </div>

            <div v-if="pendingDetailsEvent" class="details-event-row">
              <div class="details-event-main flex-grow-1">
                <div class="d-flex align-items-center">
                  <HourMinutePicker
                    v-model="pendingDetailsEvent.time"
                    class="mr-2 details-time-input"
                  />
                  <b-form-select
                    v-model="pendingDetailsEvent.type"
                    :options="eventTypes"
                    class="mr-2 details-type-input"
                  ></b-form-select>
                  <b-button
                    variant="outline-success"
                    class="mr-1"
                    @click="validatePendingDetailsEvent"
                  >
                    <b-icon icon="check"></b-icon>
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    @click="cancelPendingDetailsEvent"
                  >
                    <b-icon icon="x"></b-icon>
                  </b-button>
                </div>
              </div>
            </div>

            <b-alert v-if="detailsError" variant="danger" show class="mt-3 mb-0">
              {{ detailsError }}
            </b-alert>

            <div class="text-right mt-3">
              <b-button variant="secondary" size="sm" @click="detailsModalVisible = false">
                Fermer
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="ml-2"
                :disabled="detailsSaving || pendingDetailsEvent !== null"
                @click="saveDetailsEvents"
              >
                <b-spinner v-if="detailsSaving" small class="mr-2"></b-spinner>
                Enregistrer
              </b-button>
            </div>
          </b-card>
        </div>
      </b-modal>

      <b-modal
        v-model="successModalVisible"
        title="Permanence créée !"
        hide-footer
        centered
      >
        <div class="text-center py-3">
          <b-icon icon="check-circle-fill" variant="success" font-scale="3" class="mb-3"></b-icon>
          <p class="mb-4">Ta permanence a bien été enregistrée.  <br> Clique sur le bouton dessous pour t'y inscrire dès maintenant et proposer (ou non) un covoiturage !</p>
          <div class="d-flex justify-content-center" style="gap: 0.75rem;">
            <b-button variant="outline-secondary" @click="successModalVisible = false">Plus tard</b-button>
            <b-button variant="primary" @click="registerToCreatedOpening">
              <b-icon icon="calendar-check" class="mr-1"></b-icon>
              S'inscrire à ma permanence
            </b-button>
          </div>
        </div>
      </b-modal>

      <AddOpeningModal
        :visible="addOpeningVisible"
        :existing-dates="existingOpeningDates"
        @update:visible="addOpeningVisible = $event"
        @hidden="addOpeningVisible = false"
        @created="handleOpeningCreated"
      />

      <b-modal
        v-model="localCodeModalVisible"
        title="Code du local"
        hide-footer
        centered
        @hidden="resetLocalCodeModal"
      >
        <template v-if="localCodeRevealed">
          <div class="local-code-card">
            <div class="local-code-card__header">
              <b-icon icon="shield-lock-fill" class="mr-1" variant="primary"></b-icon>
              <span>Code du local</span>
            </div>

            <div class="local-code-digits" :class="{ 'local-code-digits--ending': localCodeCountdown <= 10 }">
              <span
                v-for="(digit, index) in localCodeDigits"
                :key="`code-digit-${index}`"
                class="local-code-digit"
              >
                {{ digit }}
              </span>
            </div>

            <div class="local-code-progress">
              <div
                class="local-code-progress__bar"
                :class="progressBarClass"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
            <div class="local-code-progress__label">
              <b-icon icon="clock" class="mr-1"></b-icon>
              Masqué dans {{ localCodeCountdown }} seconde{{ localCodeCountdown > 1 ? 's' : '' }}
            </div>
          </div>

          <div class="text-center mt-3">
            <b-button variant="secondary" @click="localCodeModalVisible = false">
              Fermer
            </b-button>
          </div>
        </template>

        <template v-else>
          <p class="text-muted mb-3">
            Confirme ton mot de passe pour afficher le code du local. Le code restera affiché pendant 1 minute.
          </p>
          <b-form @submit.prevent="submitLocalCodePassword">
            <b-form-group label="Mot de passe" label-for="local-code-password">
              <b-form-input
                id="local-code-password"
                v-model="localCodePassword"
                type="password"
                autocomplete="current-password"
                :disabled="localCodeLoading"
                autofocus
              />
            </b-form-group>

            <b-alert v-if="localCodeError" variant="danger" show class="mb-3">
              {{ localCodeError }}
            </b-alert>

            <div class="text-right">
              <b-button variant="secondary" class="mr-2" @click="localCodeModalVisible = false">
                Annuler
              </b-button>
              <b-button
                variant="primary"
                type="submit"
                :disabled="!localCodePassword || localCodeLoading"
              >
                <b-spinner v-if="localCodeLoading" small class="mr-2"></b-spinner>
                Afficher le code
              </b-button>
            </div>
          </b-form>
        </template>
      </b-modal>
    </div>

    <div v-else class="restricted-state text-center">
      <p class="text-muted">
        Pour proposer tes propres permanences, rapproche-toi du bureau de l’association afin de suivre
        la formation ouvreur.
      </p>

      <b-card class="mt-4 text-left mx-auto" style="border-radius: 0.75rem;">
        <h6 class="font-weight-bold mb-3">Guide des ouvreurs</h6>
        <p class="text-muted small mb-3">
          En attendant, tu peux déjà lire le guide de l’ouvreur !
        </p>
        <a
          :href="guideOuvreurUrl"
          download="guide-ouvreur-guc-voile.pdf"
          class="btn btn-outline-primary btn-sm mb-3"
        >
          <b-icon icon="file-earmark-pdf" class="mr-1"></b-icon>
          Télécharger le guide des ouvreurs
        </a>
      </b-card>

      <b-button
        variant="primary"
        class="mt-4"
        href="mailto:gucvoile@gmail.com"
      >
        Contacter le bureau
      </b-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AddOpeningModal from "@/components/AddOpeningModal.vue";
import HourMinutePicker from "@/components/HourMinutePicker.vue";
import session from "@/store/session";
import OpeningService from "@/services/opening.service";

export default {
  name: "MyOpenings",
  components: {
    AddOpeningModal,
    HourMinutePicker
  },
  data() {
    return {
      MyOpenings: [],
      OtherOpenings: [],
      PastOpenings: [],
      loading: false,
      error: null,
      addOpeningVisible: false,
      createdOpeningId: null,
      successModalVisible: false,
      guideOuvreurRead: false,
      confirmDeleteText: "",
      confirmedOpening: null,
      detailsModalVisible: false,
      selectedOpening: null,
      detailsForm: {
        events: []
      },
      detailsSaving: false,
      detailsError: null,
      pendingDetailsEvent: null,
      eventTypes: [
        { value: "ouverture", text: "Ouverture" },
        { value: "fermeture", text: "Fermeture" }
      ],
      localCodeModalVisible: false,
      localCodePassword: "",
      localCodeError: null,
      localCodeLoading: false,
      localCodeRevealed: null,
      localCodeCountdown: 0,
      localCodeDuration: 60,
      localCodeTimer: null,
    };
  },
  beforeDestroy() {
    this.clearLocalCodeTimer();
  },
  computed: {
    user() {
      return session.user;
    },
    guideOuvreurUrl() {
      return (axios.defaults.baseURL || '') + 'app-settings/guide-ouvreur';
    },
    currentUserId() {
      if (!this.user) return null;
      return this.user.id;
    },
    confirmedOpeningHasOtherEvents() {
      if (!this.confirmedOpening || !Array.isArray(this.confirmedOpening.openingEvents)) return false;
      return this.confirmedOpening.openingEvents.some(
        event => String(this.getEventUserId(event && event.user)) !== String(this.currentUserId)
      );
    },
    isAuthorized() {
      if (!this.user || !this.user.role) return false;
      return ["ouvreur", "admin"].includes(this.user.role);
    },
    isAdmin() {
      return !!(this.user && this.user.role === "admin");
    },
    localCodeDigits() {
      return this.localCodeRevealed ? this.localCodeRevealed.split("") : [];
    },
    progressPercent() {
      if (!this.localCodeDuration) return 0;
      return Math.max(0, Math.min(100, (this.localCodeCountdown / this.localCodeDuration) * 100));
    },
    progressBarClass() {
      if (this.localCodeCountdown <= 10) return "local-code-progress__bar--danger";
      if (this.localCodeCountdown <= 30) return "local-code-progress__bar--warning";
      return "local-code-progress__bar--ok";
    },
    existingOpeningDates() {
      const allOpenings = [
        ...this.MyOpenings,
        ...this.OtherOpenings,
        ...this.PastOpenings
      ];
      return [
        ...new Set(
          allOpenings
            .map(opening => (opening.date ? String(opening.date).split("T")[0] : null))
            .filter(Boolean)
        )
      ];
    },
    // myUpcomingOpenings() {
    //   return this.openings
    //     .filter(opening => !isOpeningPast(opening) && this.openingHasCurrentUserEvent(opening))
    //     .sort((a, b) => this.getOpeningDate(a) - this.getOpeningDate(b));
    // },
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (!user) return;
        if (this.isAuthorized) {
          this.fetchOpenings();
        } else {
          this.loading = false;
        }
      }
    }
  },
  methods: {
    handleOpeningCreated(openingId) {
      this.createdOpeningId = openingId;
      this.successModalVisible = true;
      this.fetchOpenings();
    },
    registerToCreatedOpening() {
      this.successModalVisible = false;
      this.$router.push({ name: 'home', query: { openOpening: this.createdOpeningId } });
    },
    async fetchOpenings() {
      if (!this.isAuthorized) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = null;

      try {
        const res = await OpeningService.getAllOuvreur();

        if (res.status !== 200 || res.data.error) {
          throw new Error("Impossible de récupérer vos permanences.");
        }
        
        this.MyOpenings = res.data.filter(opening => !this.isOpeningPast(opening) && this.openingHasCurrentUserEvent(opening)) || [];
        this.OtherOpenings = res.data.filter(opening => !this.isOpeningPast(opening) && !this.openingHasCurrentUserEvent(opening)) || [];
        this.PastOpenings = res.data.filter(opening => this.isOpeningPast(opening)) || [];

        console.log("Openings récupérées :", res.data);

      } catch (err) {
        console.error("Erreur mes permanences :", err);
        this.error = "Une erreur inattendue est survenue.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return "Date à confirmer";
      return new Date(date).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long"
      });
    },
    formatTime(time) {
      if (!time) return "Heure à définir";
      const date = new Date(time);
      if (Number.isNaN(date.getTime())) return "Heure à définir";
      return date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Paris",
        hour12: false,
      }).replace(":", "h");
    },
    formatTimeRange(start, end) {
      if (!start && !end) return "Horaire à définir";
      if (start && end) return `${start} - ${end}`;
      return start || end;
    },
    getOpeningTypeLabel(type) {
      if (type === "ADMIN_EVENT") return "Evenement admin";
      if (type === "MEMBER_OPENING") return "Permanence membre";
      if (type === "DAY_BLOCKED") return "Jour bloque";
      return type || "Type inconnu";
    },
    getOpeningTypeVariant(type) {
      if (type === "ADMIN_EVENT") return "primary";
      if (type === "MEMBER_OPENING") return "success";
      if (type === "DAY_BLOCKED") return "warning";
      return "secondary";
    },
    getEventCreatorLabel(event) {
      if (!event || !event.user) return "Non défini";
      const name = [event.user.name, event.user.surname].filter(Boolean).join(" ").trim();
      if (name) return name;
      return event.user.email || "Non défini";
    },
    normalizeTimeValue(time) {
      if (!time) return "12:00:00";
      if (typeof time === "string") {
        if (time.includes("T")) {
          const date = new Date(time);
          if (Number.isNaN(date.getTime())) return "12:00:00";
          return date.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZone: "Europe/Paris",
            hour12: false,
          });
        }
        if (time.length === 5) return `${time}:00`;
        return time;
      }
      if (time instanceof Date && !Number.isNaN(time.getTime())) {
        return time.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZone: "Europe/Paris",
          hour12: false,
        });
      }
      return "12:00:00";
    },
    getEventUserId(user) {
      if (!user) return null;
      if (typeof user === "string" || typeof user === "number") return user;
      return user.id || user._id || null;
    },
    canCurrentUserDeleteEvent(event) {
      const creatorId = this.getEventUserId(event && event.user);
      if (!creatorId || !this.currentUserId) return false;
      return String(creatorId) === String(this.currentUserId);
    },
    sortDetailsEvents() {
      this.detailsForm.events.sort((a, b) => {
        const aTime = a.time || "";
        const bTime = b.time || "";
        return aTime.localeCompare(bTime);
      });
    },
    addDetailsEvent() {
      if (this.pendingDetailsEvent) return;
      this.pendingDetailsEvent = { time: "12:00:00", type: "ouverture", user: session.user || null };
    },
    validatePendingDetailsEvent() {
      if (!this.pendingDetailsEvent) return;
      this.detailsForm.events.push({ ...this.pendingDetailsEvent });
      this.pendingDetailsEvent = null;
      this.sortDetailsEvents();
    },
    cancelPendingDetailsEvent() {
      this.pendingDetailsEvent = null;
    },
    removeDetailsEvent(index) {
      if (this.detailsForm.events.length <= 2) return;
      const event = this.detailsForm.events[index];
      if (!this.canCurrentUserDeleteEvent(event)) return;
      this.detailsForm.events.splice(index, 1);
    },
    getParticipantsCount(opening) {
      if (!opening) return 0;
      const participants = Number.isFinite(opening.participantsCount)
        ? opening.participantsCount
        : (Array.isArray(opening.participants) ? opening.participants.length : 0);
      const preregistrations = Number.isFinite(opening.preregistrationsCount)
        ? opening.preregistrationsCount
        : (Array.isArray(opening.preregistrations) ? opening.preregistrations.length : 0);
      return participants + preregistrations;
    },
    openingHasCurrentUserEvent(opening) {
      if (!opening || !Array.isArray(opening.openingEvents)) return false;
      return opening.openingEvents.some(event => event.user._id === this.currentUserId);
    },
    getOpeningDate(opening) {
      if (!opening) return new Date(0);
      const eventTime =
        opening.openingEvents && opening.openingEvents.length
          ? opening.openingEvents[0].time
          : opening.date;
      return eventTime ? new Date(eventTime) : new Date(0);
    },
    isOpeningPast(opening) {
      const now = new Date();
      const openingDate = new Date(opening.date);
      openingDate.setHours(23, 59, 59, 999);
      return openingDate < now;
    },
    goToOpening(opening) {
      if (opening.id) {
        this.$router.push(`/ouverture/${opening.id}`);
      } 
    },
    openDeleteConfirmation(opening) {
      this.$bvModal.show('confirm-delete-modal');
      this.confirmDeleteText="";
      this.confirmedOpening = opening;
    },
    canDeleteOpening(opening) {
      if (!opening || !Array.isArray(opening.owners) || !this.currentUserId) return false;
      return opening.owners.some(owner => owner && String(owner._id) === String(this.currentUserId));
    },
    openDetails(opening) {
      this.selectedOpening = opening || null;
      const events = this.selectedOpening && Array.isArray(this.selectedOpening.openingEvents)
        ? this.selectedOpening.openingEvents
        : [];
      this.detailsForm.events = events.map(event => ({
        _id: event._id,
        time: this.normalizeTimeValue(event.time),
        type: event.type || "ouverture",
        user: event.user || null
      }));
      if (!this.detailsForm.events.length) {
        this.detailsForm.events = [
          { time: "12:00:00", type: "ouverture" },
          { time: "18:00:00", type: "fermeture" }
        ];
      }
      this.detailsError = null;
      this.detailsSaving = false;
      this.pendingDetailsEvent = null;
      this.detailsModalVisible = true;
    },
    async saveDetailsEvents() {
      if (!this.selectedOpening) return;
      const openingId = this.selectedOpening.id || this.selectedOpening._id;
      if (!openingId) return;

      const invalidEvent = this.detailsForm.events.find(event => !event.time || !event.type);
      if (invalidEvent) {
        this.detailsError = "Veuillez renseigner l'heure et le type pour chaque événement.";
        return;
      }

      this.detailsSaving = true;
      this.detailsError = null;

      try {
        const originals = this.selectedOpening.openingEvents || [];
        const originalById = new Map(originals.map(e => [String(e._id), e]));
        const localIds = new Set();

        const added = [];
        const modified = [];

        for (const local of this.detailsForm.events) {
          const time = this.normalizeTimeValue(local.time);
          if (!local._id) {
            added.push({ type: local.type, time });
            continue;
          }
          localIds.add(String(local._id));
          const orig = originalById.get(String(local._id));
          if (!orig) continue;
          const typeChanged = orig.type !== local.type;
          const timeChanged = this.normalizeTimeValue(orig.time) !== time;
          if (typeChanged || timeChanged) {
            modified.push({ _id: local._id, type: local.type, time });
          }
        }

        const removed = originals
          .filter(orig => !localIds.has(String(orig._id)))
          .map(orig => orig._id);

        if (added.length || modified.length || removed.length) {
          await OpeningService.updateOpeningEvents(openingId, { added, modified, removed });
          await this.fetchOpenings();
          const refreshed = [...this.MyOpenings, ...this.OtherOpenings, ...this.PastOpenings]
            .find(item => (item.id || item._id) === openingId);
          if (refreshed) {
            this.selectedOpening = refreshed;
          }
        }
        this.detailsModalVisible = false;
      } catch (error) {
        console.error("Erreur lors de la mise à jour des événements :", error);
        this.detailsError = "Impossible de mettre à jour les événements.";
      } finally {
        this.detailsSaving = false;
      }
    },
    confirmModalClosed() {
      this.confirmDeleteText="";
      this.confirmedOpening = null;
    },
    deleteOpening(opening) {
      try {
        OpeningService.remove(opening.id).then(() => {
          this.fetchOpenings();
        });
      } catch (error) {
        console.error("Erreur lors de la suppression de la permanence :", error);
        this.error = "Une erreur est survenue lors de la suppression de la permanence.";
      }

      this.$bvModal.hide('confirm-delete-modal');
    },
    openLocalCodeModal() {
      this.resetLocalCodeModal();
      this.localCodeModalVisible = true;
    },
    resetLocalCodeModal() {
      this.clearLocalCodeTimer();
      this.localCodePassword = "";
      this.localCodeError = null;
      this.localCodeLoading = false;
      this.localCodeRevealed = null;
      this.localCodeCountdown = 0;
    },
    clearLocalCodeTimer() {
      if (this.localCodeTimer) {
        clearInterval(this.localCodeTimer);
        this.localCodeTimer = null;
      }
    },
    async submitLocalCodePassword() {
      if (!this.localCodePassword) return;
      this.localCodeLoading = true;
      this.localCodeError = null;
      try {
        const res = await axios.post(
          "app-settings/code-local/reveal",
          { password: this.localCodePassword }
        );
        const code = res.data && res.data.code;
        const seconds = (res.data && res.data.expiresInSeconds) || 60;
        if (!code) {
          this.localCodeError = "Aucun code n'est défini pour le moment.";
          return;
        }
        this.localCodePassword = "";
        this.localCodeRevealed = code;
        this.localCodeDuration = seconds;
        this.localCodeCountdown = seconds;
        this.localCodeTimer = setInterval(() => {
          this.localCodeCountdown -= 1;
          if (this.localCodeCountdown <= 0) {
            this.clearLocalCodeTimer();
            this.localCodeModalVisible = false;
          }
        }, 1000);
      } catch (err) {
        const status = err && err.response && err.response.status;
        const message = err && err.response && err.response.data && (err.response.data.message || err.response.data.error);
        if (status === 401) {
          this.localCodeError = message || "Mot de passe incorrect.";
        } else if (status === 404) {
          this.localCodeError = "Aucun code n'est défini pour le moment.";
        } else if (status === 429) {
          this.localCodeError = message || "Trop de tentatives. Réessaie dans quelques minutes.";
        } else {
          this.localCodeError = message || "Impossible de récupérer le code.";
        }
      } finally {
        this.localCodeLoading = false;
      }
    },
  }
};
</script>

<style scoped>
.my-openings h2 {
  font-weight: 600;
}

.opening-card {
  border: 0;
  box-shadow: 0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07);
  border-radius: 0.75rem;
}

.opening-card h5 {
  font-weight: 600;
}

.opening-mobile-card {
  border: 1px solid #eef1f5;
  border-radius: 0.75rem;
  box-shadow: none;
}

.opening-mobile-card__title {
  font-weight: 600;
}

.opening-mobile-card__actions {
  display: inline-flex;
  align-items: center;
}

.opening-mobile-action-btn {
  line-height: 1;
}

.opening-mobile-card__meta {
  border-top: 1px solid #f1f3f6;
  padding-top: 0.5rem;
}

.opening-mobile-card__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.2rem 0;
}

.opening-mobile-card__label {
  color: #6b7280;
  font-size: 0.85rem;
}

.opening-mobile-card__value {
  font-weight: 600;
  text-align: right;
}

.details-modal .details-header h4 {
  font-weight: 600;
}

.details-meta {
  gap: 1rem;
}

.details-meta .meta-item {
  background: #f8fafc;
  border: 1px solid #edf1f6;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  min-width: 140px;
}

.details-meta .meta-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.details-card {
  border: 1px solid #eef1f5;
  box-shadow: none;
}

.details-event-row {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f3f6;
}

.details-event-main {
  width: 100%;
}

.details-time-input {
  width: 150px;
}

.details-type-input {
  width: 140px;
}

.details-event-row:last-child {
  border-bottom: 0;
}

.event-row--locked {
  opacity: 0.45;
  pointer-events: none;
}

@media (max-width: 576px) {
  .details-time-input,
  .details-type-input {
    width: 100%;
  }
}

.action-menu-toggle {
  border: 0 !important;
  box-shadow: none !important;
}

.action-menu-toggle:focus,
.action-menu-toggle:focus-visible {
  box-shadow: none !important;
}

::v-deep .action-menu .dropdown-toggle {
  border: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}

::v-deep .action-menu .dropdown-toggle:hover,
::v-deep .action-menu .dropdown-toggle:focus,
::v-deep .action-menu .dropdown-toggle:active {
  background: transparent !important;
  box-shadow: none !important;
}

::v-deep .action-menu .dropdown-menu {
  z-index: 2000;
}

.openings-section + .openings-section {
  border-top: 1px solid #f0f3f8;
  padding-top: 2rem;
}

.section-title {
  font-weight: 600;
}

::v-deep .alert-info {
  background-color: #e9f7fc;
  border-color: #bee5eb;
  color: #0b3f4a;
}

::v-deep .alert-danger {
  background-color: #fdecea;
  border-color: #f5c6cb;
  color: #6b1015;
}

::v-deep .alert strong {
  color: inherit;
}

.local-code-card {
  background: linear-gradient(135deg, #f8fafc 0%, #eef3f9 100%);
  border: 1px solid #e1e6ed;
  border-radius: 1rem;
  padding: 1.25rem 1rem 1rem;
  box-shadow: 0 4px 18px rgba(50, 50, 93, 0.06);
}

.local-code-card__header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.05rem;
  text-transform: uppercase;
  color: #5b6577;
  margin-bottom: 1rem;
}

.local-code-digits {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  transition: transform 0.2s ease;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.local-code-digit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  height: 3.5rem;
  padding: 0 0.4rem;
  font-family: "Courier New", Courier, monospace;
  font-size: 2rem;
  font-weight: 700;
  color: #1a2a45;
  background: #ffffff;
  border: 1px solid #d6dde7;
  border-radius: 0.55rem;
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.04);
}

.local-code-digits--ending .local-code-digit {
  color: #b53b3b;
  border-color: #f1c6c6;
  animation: local-code-pulse 1s ease-in-out infinite;
}

@keyframes local-code-pulse {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}

.local-code-progress {
  height: 6px;
  background: #e1e6ed;
  border-radius: 999px;
  overflow: hidden;
}

.local-code-progress__bar {
  height: 100%;
  border-radius: 999px;
  transition: width 1s linear, background-color 0.3s ease;
}

.local-code-progress__bar--ok      { background: #34c759; }
.local-code-progress__bar--warning { background: #f5a623; }
.local-code-progress__bar--danger  { background: #e74c3c; }

.local-code-progress__label {
  margin-top: 0.4rem;
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
}

@media (max-width: 480px) {
  .local-code-digit {
    min-width: 2.2rem;
    height: 3rem;
    font-size: 1.6rem;
  }
}

.restricted-state {
  max-width: 600px;
  margin: 0 auto;
  padding: 3rem 1rem;
}

.restricted-state .alert-warning {
  background-color: var(--color-warning);
  border-color: var(--color-warning-border);
  color: var(--color-warning-text);
}
</style>
